/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Label } from 'reactstrap';
import { toast } from 'react-toastify';
import {
  APIProvider,
  AdvancedMarker,
  Map,
  Pin,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';
import api from '~/services/api';
import { stateList } from '~/util/stateList';

const DEFAULT_CENTER = { lat: -23.55052, lng: -46.633308 };

const onlyDigits = value => (value || '').replace(/\D/g, '');

const formatCEP = value => {
  const digits = onlyDigits(value).slice(0, 8);

  if (digits.length !== 8) return digits;

  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const toNumberOrNull = value => {
  if (value === null || value === undefined || value === '') return null;

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeLatLng = value => {
  const lat = toNumberOrNull(value?.lat);
  const lng = toNumberOrNull(value?.lng);

  if (lat === null || lng === null) return null;

  return { lat, lng };
};

const normalizeText = value =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const readComponentText = (component, short = false) => {
  if (!component) return '';

  if (short) {
    return component.short_name || component.shortText || '';
  }

  return component.long_name || component.longText || '';
};

const getAddressComponent = (
  components = [],
  types = [],
  { short = false } = {}
) => {
  const match = components.find(component =>
    types.some(type => (component.types || []).includes(type))
  );

  return match ? readComponentText(match, short) : '';
};

const parseAddressComponents = (components = [], formattedAddress = '') => ({
  cep: formatCEP(getAddressComponent(components, ['postal_code'])),
  street_name: getAddressComponent(components, ['route']),
  number: getAddressComponent(components, ['street_number']),
  neighborhood: getAddressComponent(components, [
    'sublocality_level_1',
    'sublocality',
    'neighborhood',
  ]),
  city: getAddressComponent(components, [
    'locality',
    'administrative_area_level_2',
    'administrative_area_level_3',
  ]),
  state: getAddressComponent(components, ['administrative_area_level_1'], {
    short: true,
  }),
  formatted_address: formattedAddress || '',
});

const formatResolvedAddressLabel = address => {
  const parts = [
    address.street_name,
    address.number && address.number !== 'sn' ? address.number : '',
    address.neighborhood,
    address.city,
    address.state,
    address.cep,
  ].filter(Boolean);

  return parts.join(', ');
};

const extractLatLng = event => {
  if (event?.detail?.latLng) {
    return event.detail.latLng;
  }

  if (!event?.latLng) return null;

  if (typeof event.latLng.toJSON === 'function') {
    return event.latLng.toJSON();
  }

  if (
    typeof event.latLng.lat === 'function' &&
    typeof event.latLng.lng === 'function'
  ) {
    return {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };
  }

  return event.latLng;
};

const pickGeocodeResults = (
  strictResponse,
  countryOnlyResponse,
  cepOnlyResponse
) => {
  if (strictResponse?.results?.length) {
    return strictResponse.results;
  }

  if (countryOnlyResponse?.results?.length) {
    return countryOnlyResponse.results;
  }

  if (cepOnlyResponse?.results?.length) {
    return cepOnlyResponse.results;
  }

  return [];
};

const MapInstanceBridge = ({ onMapReady }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    onMapReady(map);
  }, [map, onMapReady]);

  return null;
};

const StoreLocationPickerContent = ({
  value,
  formRef,
  selectedState,
  setSelectedState,
  selectedCity,
  setSelectedCity,
  addressNumber,
  setAddressNumber,
  noNumber,
  setNoNumber,
  setExtraPersistenceFields,
  onLocationChange,
  pinMovedManually,
  setPinMovedManually,
  addressSyncState,
  setAddressSyncState,
  addressGeocodeTrigger,
  internalAddressUpdateRef,
  mapHeight = 320,
}) => {
  const placesLibrary = useMapsLibrary('places');
  const geocodingLibrary = useMapsLibrary('geocoding');

  const searchInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const lastCepLookupRef = useRef('');

  const initialLocation = useMemo(() => normalizeLatLng(value), [value]);

  const [geocoder, setGeocoder] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [markerPosition, setMarkerPosition] = useState(initialLocation || null);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    if (!geocodingLibrary) return;

    setGeocoder(new geocodingLibrary.Geocoder());
  }, [geocodingLibrary]);

  const centerMap = useCallback(
    (position, zoom = 17) => {
      if (!mapInstance || !position?.lat || !position?.lng) return;

      mapInstance.panTo(position);
      mapInstance.setZoom(zoom);
    },
    [mapInstance]
  );

  useEffect(() => {
    const normalizedValue = normalizeLatLng(value);

    if (!normalizedValue) return;

    setMarkerPosition(normalizedValue);
  }, [value]);

  useEffect(() => {
    if (!markerPosition?.lat || !markerPosition?.lng) return;

    centerMap(markerPosition, 17);
  }, [centerMap, markerPosition]);

  const getCurrentAddress = useCallback(() => {
    const formData = formRef.current?.getData() || {};
    const address = formData.address || {};

    return {
      ...address,
      city: address.city || selectedCity?.value || '',
      state: address.state || selectedState?.value || '',
      number: address.number || (noNumber ? 'sn' : addressNumber) || '',
    };
  }, [addressNumber, formRef, noNumber, selectedCity, selectedState]);

  const buildAddressString = useCallback(() => {
    const address = getCurrentAddress();
    const number =
      address.number && address.number !== 'sn' ? address.number : '';

    return [
      address.street_name,
      number,
      address.neighborhood,
      address.city,
      address.state,
    ]
      .filter(Boolean)
      .join(', ');
  }, [getCurrentAddress]);

  useEffect(() => {
    if (searchValue) return;

    const address = getCurrentAddress();
    const initialSearch = address.cep || formatResolvedAddressLabel(address);

    if (initialSearch) {
      setSearchValue(initialSearch);
    }
  }, [getCurrentAddress, searchValue]);

  const releaseInternalUpdateLock = useCallback(() => {
    window.setTimeout(() => {
      internalAddressUpdateRef.current = false;
    }, 0);
  }, [internalAddressUpdateRef]);

  const applyResolvedAddressToForm = useCallback(
    resolvedAddress => {
      if (!formRef.current) return;

      const formData = formRef.current.getData() || {};
      const currentAddress = formData.address || {};

      const sameStreet =
        normalizeText(currentAddress.street_name) ===
        normalizeText(resolvedAddress.street_name);

      const nextNumber =
        resolvedAddress.number ||
        (sameStreet ? currentAddress.number || addressNumber || '' : '');

      internalAddressUpdateRef.current = true;

      formRef.current.setData({
        ...formData,
        address: {
          ...currentAddress,
          cep: resolvedAddress.cep || currentAddress.cep || '',
          street_name:
            resolvedAddress.street_name || currentAddress.street_name || '',
          number: nextNumber,
          neighborhood:
            resolvedAddress.neighborhood || currentAddress.neighborhood || '',
          city: resolvedAddress.city || currentAddress.city || '',
          state: resolvedAddress.state || currentAddress.state || '',
        },
      });

      setExtraPersistenceFields(state => ({
        ...state,
        cep: resolvedAddress.cep || state.cep || '',
      }));

      if (resolvedAddress.state) {
        const nextState = stateList.find(
          item => item.value === resolvedAddress.state
        ) || {
          label: resolvedAddress.state,
          value: resolvedAddress.state,
        };

        setSelectedState(nextState);
      }

      if (resolvedAddress.city) {
        setSelectedCity({
          label: resolvedAddress.city,
          value: resolvedAddress.city,
        });
      }

      if (resolvedAddress.number) {
        setNoNumber(false);
        setAddressNumber(resolvedAddress.number);
      } else if (!sameStreet && !noNumber) {
        // Sem número resolvido: se o operador marcou "Sem Número",
        // respeita a escolha dele em vez de desmarcar.
        setAddressNumber('');
      }

      setSearchValue(
        resolvedAddress.formatted_address ||
          formatResolvedAddressLabel(resolvedAddress) ||
          resolvedAddress.cep ||
          ''
      );

      releaseInternalUpdateLock();
    },
    [
      addressNumber,
      formRef,
      internalAddressUpdateRef,
      releaseInternalUpdateLock,
      setAddressNumber,
      setExtraPersistenceFields,
      setNoNumber,
      noNumber,
      setSelectedCity,
      setSelectedState,
    ]
  );

  const reverseGeocodePosition = useCallback(
    async position => {
      if (!geocoder || !position?.lat || !position?.lng) return;

      setLoading(true);

      try {
        const { results } = await geocoder.geocode({
          location: position,
          language: 'pt-BR',
          region: 'br',
        });

        const result = results?.[0];

        if (!result) {
          toast.error('Não foi possível identificar o endereço desse ponto.');
          return;
        }

        const resolvedAddress = parseAddressComponents(
          result.address_components || [],
          result.formatted_address || ''
        );

        setMarkerPosition(position);
        applyResolvedAddressToForm(resolvedAddress);
        onLocationChange(position);
        setPinMovedManually(true);
        setAddressSyncState('synced');
      } catch (error) {
        toast.error('Não foi possível atualizar o endereço pelo mapa.');
      } finally {
        setLoading(false);
      }
    },
    [
      applyResolvedAddressToForm,
      geocoder,
      onLocationChange,
      setAddressSyncState,
      setPinMovedManually,
    ]
  );

  const geocodeSafely = useCallback(
    async request => {
      try {
        return await geocoder.geocode(request);
      } catch (error) {
        const message = String(error?.message || error || '');

        if (
          error?.code === 'ZERO_RESULTS' ||
          message.includes('ZERO_RESULTS')
        ) {
          return null;
        }

        throw error;
      }
    },
    [geocoder]
  );

  const geocodeCurrentAddress = useCallback(
    async ({ force = false } = {}) => {
      if (!geocoder || !formRef.current) return;
      if (pinMovedManually && !force) return;

      const address = getCurrentAddress();
      const addressString = buildAddressString();
      const cepDigits = onlyDigits(address.cep);

      if (!addressString && !cepDigits) {
        toast.warning('Preencha o endereço antes de recentralizar o mapa.');
        return;
      }

      setLoading(true);

      try {
        const strictResponse = addressString
          ? await geocodeSafely({
              address: addressString,
              componentRestrictions: cepDigits
                ? {
                    country: 'BR',
                    postalCode: cepDigits,
                  }
                : {
                    country: 'BR',
                  },
              language: 'pt-BR',
              region: 'br',
            })
          : null;

        const countryOnlyResponse =
          !strictResponse?.results?.length && addressString
            ? await geocodeSafely({
                address: addressString,
                componentRestrictions: {
                  country: 'BR',
                },
                language: 'pt-BR',
                region: 'br',
              })
            : null;

        const cepOnlyResponse =
          !strictResponse?.results?.length &&
          !countryOnlyResponse?.results?.length &&
          cepDigits
            ? await geocodeSafely({
                address: formatCEP(cepDigits),
                componentRestrictions: {
                  country: 'BR',
                },
                language: 'pt-BR',
                region: 'br',
              })
            : null;

        const results = pickGeocodeResults(
          strictResponse,
          countryOnlyResponse,
          cepOnlyResponse
        );

        if (!results.length) {
          toast.warning(
            'Não foi possível posicionar esse endereço no mapa automaticamente. Ajuste manualmente no mapa.'
          );
          return;
        }

        const [firstResult] = results;

        const nextPosition = {
          lat: firstResult.geometry.location.lat(),
          lng: firstResult.geometry.location.lng(),
        };

        setMarkerPosition(nextPosition);
        onLocationChange(nextPosition);
        setAddressSyncState('synced');

        if (force) {
          setPinMovedManually(false);
        }
      } catch (error) {
        const status = error?.code || error?.message || 'UNKNOWN_ERROR';

        console.error('Erro ao geocodificar endereço da loja', {
          status,
          addressString,
          cepDigits,
          error,
        });

        toast.error(
          `Não foi possível recentralizar o mapa pelo endereço (${status}).`
        );
      } finally {
        setLoading(false);
      }
    },
    [
      buildAddressString,
      formRef,
      geocodeSafely,
      geocoder,
      getCurrentAddress,
      onLocationChange,
      pinMovedManually,
      setAddressSyncState,
      setPinMovedManually,
    ]
  );

  const lookupCepAndSync = useCallback(
    async rawValue => {
      const cepDigits = onlyDigits(rawValue);
      const formattedCep = formatCEP(cepDigits);

      if (cepDigits.length !== 8) return;
      if (lastCepLookupRef.current === cepDigits) return;

      lastCepLookupRef.current = cepDigits;
      setLoading(true);

      try {
        const { data } = await api.get(`cep/${formattedCep}`);

        const resolvedAddress = {
          cep: data.cep || formattedCep,
          street_name: data.street_name || '',
          number: data.number || '',
          neighborhood: data.neighborhood || '',
          city: data.city || '',
          state: data.state || '',
          formatted_address: formatResolvedAddressLabel({
            cep: data.cep || formattedCep,
            street_name: data.street_name || '',
            number: data.number || '',
            neighborhood: data.neighborhood || '',
            city: data.city || '',
            state: data.state || '',
          }),
        };

        applyResolvedAddressToForm(resolvedAddress);
        setPinMovedManually(false);
        setAddressSyncState('synced');

        await geocodeCurrentAddress({ force: true });
      } catch (error) {
        toast.error('Não foi possível localizar esse CEP.');
      } finally {
        setLoading(false);
      }
    },
    [
      applyResolvedAddressToForm,
      geocodeCurrentAddress,
      setAddressSyncState,
      setPinMovedManually,
    ]
  );

  useEffect(() => {
    if (!placesLibrary || !searchInputRef.current || autocompleteRef.current) {
      return undefined;
    }

    const autocomplete = new placesLibrary.Autocomplete(
      searchInputRef.current,
      {
        fields: ['address_components', 'formatted_address', 'geometry', 'name'],
        componentRestrictions: { country: ['br'] },
        types: ['address'],
      }
    );

    autocompleteRef.current = autocomplete;

    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();

      if (!place?.geometry?.location) {
        return;
      }

      const resolvedAddress = parseAddressComponents(
        place.address_components || [],
        place.formatted_address || place.name || ''
      );

      const position = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };

      setMarkerPosition(position);
      applyResolvedAddressToForm(resolvedAddress);
      onLocationChange(position);
      setPinMovedManually(false);
      setAddressSyncState('synced');

      if (place.formatted_address || place.name) {
        setSearchValue(place.formatted_address || place.name);
      }
    });

    return () => {
      if (listener) {
        listener.remove();
      }

      autocompleteRef.current = null;
    };
  }, [
    applyResolvedAddressToForm,
    onLocationChange,
    placesLibrary,
    setAddressSyncState,
    setPinMovedManually,
  ]);

  useEffect(() => {
    if (!addressGeocodeTrigger) return;

    geocodeCurrentAddress();
  }, [addressGeocodeTrigger, geocodeCurrentAddress]);

  const handleMapClick = useCallback(
    event => {
      const latLng = extractLatLng(event);

      if (!latLng) return;

      reverseGeocodePosition(latLng);
    },
    [reverseGeocodePosition]
  );

  const handleMarkerDragEnd = useCallback(
    event => {
      const latLng = extractLatLng(event);

      if (!latLng) return;

      reverseGeocodePosition(latLng);
    },
    [reverseGeocodePosition]
  );

  const handleSearchInputChange = useCallback(
    event => {
      const nextValue = event.target.value;

      setSearchValue(nextValue);

      const cepDigits = onlyDigits(nextValue);
      const isCepLikeValue =
        cepDigits.length === 8 && /^[0-9\s.-]+$/.test(nextValue);

      if (isCepLikeValue) {
        lookupCepAndSync(nextValue);
      } else {
        lastCepLookupRef.current = '';
      }
    },
    [lookupCepAndSync]
  );

  const syncLabel = useMemo(() => {
    if (addressSyncState === 'dirty') {
      return 'Endereço alterado após ajuste manual do pin.';
    }

    if (markerPosition?.lat && markerPosition?.lng) {
      return 'Mapa sincronizado com o endereço.';
    }

    return 'Informe o CEP, pesquise um endereço ou clique no mapa para definir a localização.';
  }, [addressSyncState, markerPosition]);

  return (
    <div className="border rounded p-3 mb-4">
      <div className="mb-3">
        <input
          ref={searchInputRef}
          type="text"
          className="form-control"
          placeholder="Digite o CEP ou pesquise o endereço"
          value={searchValue}
          onChange={handleSearchInputChange}
        />
      </div>

      <div className="d-flex align-items-start justify-content-between mb-2">
        <div>
          <Label className="font-weight-700 mb-1">
            Localização exata da loja
          </Label>
          <div className="text-muted font-size-12">
            Arraste o pin para o local exato onde o entregador deve chegar.
          </div>
          {markerPosition && (!addressNumber || noNumber) && (
            <div className="text-muted font-size-12 mt-1">
              Endereço sem número: o mapa posiciona pela via. Ajuste o pin no
              ponto exato — é essa coordenada que o entregador recebe.
            </div>
          )}
        </div>

        <Button
          type="button"
          color="primary"
          className="new-layout-btn new-layout-btn-transparent text-nowrap ml-3"
          style={{ flexShrink: 0 }}
          onClick={() => geocodeCurrentAddress({ force: true })}
          disabled={loading}
        >
          Recentralizar pelo endereço
        </Button>
      </div>

      {addressSyncState === 'dirty' && (
        <div className="alert alert-warning py-2 px-3 mb-3">
          Você alterou o endereço depois de ajustar o pin. Clique em{' '}
          <strong>Recentralizar pelo endereço</strong> ou mova o pin novamente.
        </div>
      )}

      <div
        style={{
          width: '100%',
          height: mapHeight,
          borderRadius: 8,
          overflow: 'hidden',
          position: 'relative',
          background: '#f8f9fa',
        }}
      >
        {loading && (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              background: 'rgba(255,255,255,0.65)',
            }}
          >
            Atualizando mapa...
          </div>
        )}

        <Map
          id="store-location-picker-map"
          mapId={process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID'}
          defaultCenter={initialLocation || DEFAULT_CENTER}
          defaultZoom={initialLocation ? 17 : 5}
          gestureHandling="greedy"
          disableDefaultUI={false}
          style={{ width: '100%', height: '100%' }}
          onClick={handleMapClick}
        >
          <MapInstanceBridge onMapReady={setMapInstance} />

          {markerPosition && (
            <AdvancedMarker
              position={markerPosition}
              draggable
              onDragEnd={handleMarkerDragEnd}
              title="Localização exata da loja"
            >
              <Pin />
            </AdvancedMarker>
          )}
        </Map>
      </div>

      <div className="d-flex align-items-center justify-content-between mt-2 text-muted font-size-12">
        <span>{syncLabel}</span>

        {markerPosition?.lat && markerPosition?.lng && (
          <span>
            {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
          </span>
        )}
      </div>
    </div>
  );
};

const StoreLocationPicker = props => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;

  if (!apiKey) {
    return (
      <div className="alert alert-warning mb-4">
        Defina <strong>REACT_APP_GOOGLE_MAPS_KEY</strong> para habilitar o mapa
        da localização da loja.
      </div>
    );
  }

  return (
    <APIProvider
      apiKey={apiKey}
      language="pt-BR"
      region="BR"
      libraries={['places']}
    >
      <StoreLocationPickerContent {...props} />
    </APIProvider>
  );
};

export default StoreLocationPicker;
